import { useCallback, useEffect, useMemo, useState, type KeyboardEvent, type JSX } from "react";
import type { components } from "../../api/schema";
import { useReaderStylePresetContext } from "../reader-style/ReaderStylePresetProvider";
import { PACING_MULTIPLIER, type ReaderPacing } from "../reader-style/types";
import { SegmentRenderer } from "./SegmentRenderer";
import { getContrastTextColor, getEndingDialogueSurface } from "./endingPalette";
import type { CharacterEntry } from "./parseInlineMarkup";
import { useReducedMotion } from "./useSegmentReveal";

type Scene = components["schemas"]["Scene"];
type Segment = components["schemas"]["Segment"];
type Character = components["schemas"]["Character"];
type TransitionPhase = "entering" | "visible" | "exiting";

const FADE_DURATION_MS = 250;
const RESTART_DELAY_MS = 800;

function calcAutoAdvanceMs(segment: Segment, pacing: ReaderPacing): number {
  return Math.max(segment.text.length * 50 * PACING_MULTIPLIER[pacing], 300);
}

export type EndingViewProps = {
  scene: Scene;
  onRestart: () => void;
  endingBackgroundColor?: string | null | undefined;
  endingTextColor?: string | null | undefined;
  /** キャラクター一覧。内部で Map に変換してセグメントの色付けに使う。省略時は空 Map 扱い。 */
  characters?: Character[];
  visualAssets?: components["schemas"]["VisualAssets"] | undefined;
};

// エンディング専用ビュー。段落は一つずつ表示し、通常シーンの配色とは独立する。
export function EndingView({
  scene,
  onRestart,
  endingBackgroundColor,
  endingTextColor,
  characters = [],
  visualAssets,
}: EndingViewProps): JSX.Element {
  const reducedMotion = useReducedMotion();
  const { preset } = useReaderStylePresetContext();
  const [activeIndex, setActiveIndex] = useState(0);
  const [phase, setPhase] = useState<TransitionPhase>(reducedMotion || scene.segments.length === 0 ? "visible" : "entering");
  const [restartVisible, setRestartVisible] = useState(false);
  const hasSegments = scene.segments.length > 0;
  const resolvedTextColor = endingTextColor ?? (endingBackgroundColor ? getContrastTextColor(endingBackgroundColor) : undefined);
  const dialogueBackgroundColor = endingBackgroundColor ? getEndingDialogueSurface(endingBackgroundColor) : undefined;
  const dialogueSurfaceForContrast = dialogueBackgroundColor ?? (preset.theme === "dark" ? "#1E293B" : "#F8FAFC");
  const dialogueTextColor = getContrastTextColor(dialogueSurfaceForContrast);
  const activeSegment = scene.segments[activeIndex];
  const isFinalSegment = !hasSegments || activeIndex === scene.segments.length - 1;
  const finished = isFinalSegment && phase === "visible";

  const charactersMap = useMemo<Map<string, CharacterEntry>>(() => {
    return new Map(
      characters.map((character) => [
        character.id,
        {
          name: character.name,
          color: character.color,
          ...(character.portraitUrl !== undefined ? { portraitUrl: character.portraitUrl } : {}),
        },
      ]),
    );
  }, [characters]);

  const advance = useCallback(() => {
    if (!activeSegment || phase === "exiting" || finished) return;
    if (reducedMotion) {
      setActiveIndex((index) => Math.min(index + 1, scene.segments.length - 1));
      return;
    }
    setPhase("exiting");
  }, [activeSegment, finished, phase, reducedMotion, scene.segments.length]);

  useEffect(() => {
    if (reducedMotion) {
      setPhase("visible");
      return;
    }
    if (phase === "entering") {
      const timeoutId = setTimeout(() => setPhase("visible"), FADE_DURATION_MS);
      return () => clearTimeout(timeoutId);
    }
    if (phase === "exiting") {
      const timeoutId = setTimeout(() => {
        setActiveIndex((index) => Math.min(index + 1, scene.segments.length - 1));
        setPhase("entering");
      }, FADE_DURATION_MS);
      return () => clearTimeout(timeoutId);
    }
    if (activeSegment && !isFinalSegment) {
      const timeoutId = setTimeout(() => setPhase("exiting"), calcAutoAdvanceMs(activeSegment, preset.pacing));
      return () => clearTimeout(timeoutId);
    }
    return undefined;
  }, [activeSegment, isFinalSegment, phase, preset.pacing, reducedMotion, scene.segments.length]);

  useEffect(() => {
    setRestartVisible(false);
    if (!finished) return;
    const timeoutId = setTimeout(() => setRestartVisible(true), RESTART_DELAY_MS);
    return () => clearTimeout(timeoutId);
  }, [finished]);

  const handleKeyDown = (event: KeyboardEvent<HTMLElement>): void => {
    if (event.target !== event.currentTarget || finished) return;
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      advance();
    }
  };

  return (
    <section
      data-testid="ending-view"
      role="region"
      className={`flex flex-col items-center gap-6 ${endingBackgroundColor ? "" : "bg-gradient-to-b from-reader-page to-reader-surface"} ${resolvedTextColor ? "" : "text-reader-primary"} px-6 py-10 rounded-lg border border-amber-500/40 animate-[fadeIn_0.5s_ease-out] motion-reduce:animate-none`}
      style={{
        ...(endingBackgroundColor ? { backgroundColor: endingBackgroundColor } : {}),
        ...(resolvedTextColor ? { color: resolvedTextColor } : {}),
      }}
      aria-label="物語の結末。クリック、タップ、Enter、または Space キーで続きを読む"
      tabIndex={finished ? undefined : 0}
      onClick={(event) => {
        if (event.target === event.currentTarget || !(event.target instanceof HTMLElement && event.target.closest("button"))) {
          advance();
        }
      }}
      onKeyDown={handleKeyDown}
    >
      <p className="tracking-wide text-2xl font-bold uppercase" style={resolvedTextColor ? { color: resolvedTextColor } : undefined}>
        — 物語の結末 —
      </p>

      {activeSegment && (
        <div
          key={activeIndex}
          className={`w-full max-w-2xl ${phase === "exiting" ? "ending-segment-exiting" : phase === "entering" ? "ending-segment-entering" : ""} ${isFinalSegment && phase !== "exiting" ? "ending-final-segment rounded-md bg-reader-surface/70 ring-1 ring-amber-500/50 px-4 py-3" : ""}`}
          style={{
            opacity: phase === "exiting" ? 0 : undefined,
            transition: phase === "exiting" && !reducedMotion ? `opacity ${FADE_DURATION_MS}ms ease-in` : undefined,
            animation: phase === "entering" && !reducedMotion ? `fadeIn ${FADE_DURATION_MS}ms ease-out` : undefined,
          }}
          data-ending-phase={phase}
        >
          <SegmentRenderer
            segment={activeSegment}
            charactersMap={charactersMap}
            visualAssets={visualAssets}
            textColorOverride={activeSegment.type === "dialogue" ? dialogueTextColor : resolvedTextColor}
            dialogueBackgroundColor={dialogueBackgroundColor}
            dialogueTextColor={dialogueTextColor}
            ensureAvatarTextContrast
            disableTypewriter
          />
        </div>
      )}

      {restartVisible && (
        <button
          type="button"
          onClick={onRestart}
          className="mt-4 px-6 py-3 rounded bg-amber-500 font-semibold hover:bg-amber-400 transition-colors text-slate-950"
        >
          新しいプレイラインを開始
        </button>
      )}
    </section>
  );
}
